import { useState } from "react";
import { Facebook, Instagram, Link as LinkIcon, Share2, Check } from "lucide-react";
import { toast } from "sonner";

/** Inline WhatsApp glyph — lucide-react has no dedicated WhatsApp icon. */
function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.92 11.92 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 6L0 24l6.18-1.62A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 22a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.67.96.98-3.58-.24-.37A9.94 9.94 0 1 1 22 12c0 5.52-4.48 10-10 10Zm5.5-7.5c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.96 1.17-.18.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.89-.79-1.5-1.77-1.67-2.07-.18-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.68-1.64-.93-2.25-.24-.59-.49-.51-.68-.52H7.7c-.2 0-.53.08-.8.38-.27.3-1.05 1.02-1.05 2.5 0 1.48 1.08 2.91 1.23 3.11.15.2 2.11 3.22 5.13 4.51.72.31 1.28.5 1.71.64.72.23 1.37.2 1.89.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.2-.57-.35Z" />
    </svg>
  );
}

function buildShareUrl(productPath) {
  if (typeof window === "undefined") return productPath;
  return `${window.location.origin}${productPath}`;
}

/**
 * Social share buttons for a product page.
 *  - Facebook: native share dialog
 *  - WhatsApp: wa.me intent (works on mobile + desktop web)
 *  - Instagram: no share URL → copies link to clipboard + suggests pasting in story/bio
 *  - "Copier le lien": clipboard fallback for any other channel
 *
 * On mobile devices that support the Web Share API, an additional native button
 * is exposed so users get the full OS picker (iMessage, Messenger, etc.).
 */
export default function ProductShareButtons({ product, productPath }) {
  const [justCopied, setJustCopied] = useState(false);
  const url = buildShareUrl(productPath);
  const title = product?.name || "Découvrez ce produit Herbalife";
  const text = `${title} — ${url}`;

  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;

  const copyLink = async (instagramHint = false) => {
    try {
      await navigator.clipboard.writeText(url);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2500);
      if (instagramHint) {
        toast.success("Lien copié — collez-le dans votre story ou votre bio Instagram");
      } else {
        toast.success("Lien copié dans le presse-papier");
      }
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title, text: title, url });
    } catch {
      /* user cancelled */
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="mt-6 pt-6 border-t border-[#E5EAE5]" data-testid="product-share">
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#4B634B] mb-3">
        Partager ce produit
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={fb}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-facebook"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#1877F2] hover:bg-[#1664CC] text-white transition-colors"
          aria-label="Partager sur Facebook"
        >
          <Facebook className="w-4 h-4" /> Facebook
        </a>

        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#25D366] hover:bg-[#1FB955] text-white transition-colors"
          aria-label="Partager sur WhatsApp"
        >
          <WhatsAppIcon className="w-4 h-4" /> WhatsApp
        </a>

        <button
          type="button"
          onClick={() => copyLink(true)}
          data-testid="share-instagram"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors"
          style={{ background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)" }}
          aria-label="Copier le lien pour Instagram"
        >
          <Instagram className="w-4 h-4" /> Instagram
        </button>

        <button
          type="button"
          onClick={() => copyLink(false)}
          data-testid="share-copy-link"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-white border border-[#E5EAE5] text-[#1A2E1A] hover:bg-[#F2F5F0] transition-colors"
          aria-label="Copier le lien"
        >
          {justCopied ? <Check className="w-4 h-4 text-[#7AC143]" /> : <LinkIcon className="w-4 h-4" />}
          {justCopied ? "Lien copié" : "Copier le lien"}
        </button>

        {canNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            data-testid="share-native"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#1A2E1A] hover:bg-[#0F1F0F] text-white transition-colors sm:hidden"
            aria-label="Partager"
          >
            <Share2 className="w-4 h-4" /> Plus…
          </button>
        )}
      </div>
      <p className="text-xs text-[#4B634B] mt-3">
        Instagram ne propose pas de lien direct depuis le web — le bouton copie l'URL pour la coller dans votre story ou votre bio.
      </p>
    </div>
  );
}
